import React from 'react';

import { useQuery } from '@apollo/client';
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import withWidth from '@material-ui/core/withWidth';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import Swiper from 'react-id-swiper';
import { Link } from 'react-router-dom';

import { GET_ALL_NEWS } from '../../graphql/news';
import { BREAKPOINTS_VALUES } from '../../theme';
import { formatDateToNewsFormat } from '../../utils/helpers';
import { LazyLoadImage } from '../LazyloadImage';
import { Loader } from '../Loader';
import { Button } from '../UI/Button';
import useStyles from './styles';

const NewsBlock = ({ data: newsBlockData, preventLoader }) => {
  const background = newsBlockData?.background;
  const classes = useStyles(background)();
  const newsLimit = newsBlockData?.news_limit;
  const buttonText = newsBlockData?.button_text;
  const buttonUrl = newsBlockData?.button_url;
  const { t } = useTranslation();

  const {
    data,
    loading,
    // error
  } = useQuery(GET_ALL_NEWS);

  if (loading) {
    return preventLoader ? null : <Loader />;
  }

  const news = [...data?.listPosts?.data]
    ?.sort((a, b) => (b.published_at > a.published_at ? 1 : -1))
    ?.slice(0, newsLimit);
  const heading = newsBlockData?.heading || t('common.noHeading');

  const swiperSettings = {
    slidesPerView: news?.length < newsLimit ? news?.length : newsLimit,
    spaceBetween: 16,
    pagination: {
      el: '.swiper-pagination',
      clickable: true,
    },
    breakpoints: {
      [BREAKPOINTS_VALUES.xl]: {
        slidesPerView: 3,
      },
      [BREAKPOINTS_VALUES.lg]: {
        slidesPerView: 3,
      },
      [BREAKPOINTS_VALUES.md]: {
        slidesPerView: 3,
      },
      [BREAKPOINTS_VALUES.sm]: {
        slidesPerView: 2.1,
      },
      [BREAKPOINTS_VALUES.xs]: {
        slidesPerView: 1.1,
        slidesOffsetAfter: 16,
      },
    },
  };

  return (
    <Box
      className={cx(
        classes.wrapper,
        background === 'Dark' ? 'dark-block' : 'light-block',
        'news-block',
      )}
    >
      <Container className={classes.container}>
        <Typography variant="h2" component="h2" align="center" className={classes.heading}>
          {heading}
        </Typography>

        <Box className={cx(classes.sliderWrapper, 'news-slider')}>
          <Swiper {...swiperSettings}>
            {news &&
              news.map((item) => {
                const { id, user, image_url, title, post_introduction, published_at } = item;

                const name = user?.name;
                const avatar = user?.avatar;
                const formattedDate = formatDateToNewsFormat(published_at);

                return (
                  <Link to={`/news/${id}`} key={id} className={classes.newsItem}>
                    {avatar ? (
                      <div className={classes.roundImageWrapper}>
                        <LazyLoadImage
                          path={avatar}
                          draggable={false}
                          alt={name}
                          srcset={[
                            { width: 50, focus: 'auto' },
                            { width: 60, focus: 'auto' },
                            { width: 70, focus: 'auto' },
                          ]}
                          sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 50px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 60px,70px`}
                          imageWrapperClass={classes.lazyAvatarWrapper}
                        />
                      </div>
                    ) : (
                      <AccountCircleIcon className={classes.roundImageWrapper} />
                    )}

                    <div className={classes.imageWrap}>
                      {image_url ? (
                        <LazyLoadImage
                          className={cx(classes.image, 'preview')}
                          path={image_url}
                          draggable={false}
                          alt={title}
                          srcset={[{ width: 440 }, { width: 360 }, { width: 350 }, { width: 340 }]}
                          sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 440px, (max-width:${BREAKPOINTS_VALUES['md']}px) 360px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 350px, 340px`}
                        />
                      ) : (
                        <div className={classes.image} />
                      )}
                    </div>

                    <div className={classes.textWrapper}>
                      <div>
                        <Typography
                          variant="h3"
                          component="h3"
                          align="left"
                          className={classes.title}
                        >
                          {title}
                        </Typography>

                        <Typography align="left" className={classes.subTitle}>
                          {post_introduction}
                        </Typography>
                      </div>

                      {name && formattedDate && (
                        <Typography align="left" className={classes.dateTitle}>
                          {`${formattedDate} • ${name}`}
                        </Typography>
                      )}
                    </div>
                  </Link>
                );
              })}
          </Swiper>
        </Box>

        <div className={classes.btnWrap}>
          {buttonText && <Button text={buttonText} link={buttonUrl} />}
        </div>
      </Container>
    </Box>
  );
};

// withWidth added here to recalculate swiper width. swiper has incorrect width after ssr
export default withWidth()(NewsBlock);
